import { createFileRoute, Link } from "@tanstack/react-router";
import {
  useTransactions,
  useCategories,
  useSubscription,
  useGoals,
  formatBRL,
  sameMonth,
  CATEGORY_COLORS,
  Category,
} from "@/lib/store";
import { useAuthCtx } from "@/lib/auth-context";
import { generateInsights } from "@/lib/insights";
import { computeForecast } from "@/lib/forecast";
import { StatCard } from "@/components/StatCard";
import { InsightCard } from "@/components/InsightCard";
import { ForecastCard } from "@/components/ForecastCard";
import { AskAIDialog } from "@/components/AskAIDialog";
import { PremiumDialog } from "@/components/PremiumDialog";
import { TransactionList } from "@/components/TransactionList";
import { DashboardSkeleton } from "@/components/skeletons";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { AIChatSpotlight } from "@/components/AIChatSpotlight";
import { Wallet, TrendingDown, TrendingUp, PiggyBank, Sparkles, ArrowRight } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getInvestments } from "@/lib/investments.functions";
import { getPaddleEnvironment } from "@/lib/paddle";

const BalanceTrendChart = lazy(() => import("@/components/charts/BalanceTrendChart"));
const CategoryPieChart = lazy(() => import("@/components/charts/CategoryPieChart"));

function ChartFallback() {
  return <div className="h-full w-full animate-pulse rounded-xl bg-muted/40" />;
}

export const Route = createFileRoute("/_app/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard — Seu Bolso Tranquilo" },
      { name: "description", content: "Acompanhe receitas, despesas, saldo e insights do seu mês em um único painel." },
      { property: "og:title", content: "Dashboard — Seu Bolso Tranquilo" },
      { property: "og:description", content: "Visão completa das suas finanças do mês: saldo, gastos por categoria e insights automáticos." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Dashboard,
  pendingComponent: DashboardSkeleton,
});

function Dashboard() {
  const { user, displayName } = useAuthCtx();
  const { transactions, loading } = useTransactions(user?.id);
  const { categories: customCategories } = useCategories(user?.id);
  const { goals } = useGoals(user?.id);
  const { isPremium, refresh: refreshSubscription } = useSubscription(user?.id);
  const getInvestmentData = useServerFn(getInvestments);
  const paymentsEnvironment = getPaddleEnvironment();
  const { data: investmentData } = useQuery({
    queryKey: ["investments", paymentsEnvironment],
    queryFn: () => getInvestmentData({ data: { environment: paymentsEnvironment } }),
    enabled: !!user?.id,
  });
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { checkout?: string };

  // Handle return from Paddle checkout — poll subscription until Premium flips on.
  useEffect(() => {
    if (search.checkout !== "success") return;
    const toastId = toast.loading("Confirmando seu pagamento…", {
      description: "Liberando seu acesso Premium.",
    });
    // Strip the search param so a refresh doesn't re-trigger this effect.
    navigate({ to: "/dashboard", replace: true });

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 12; // ~24s total
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      await refreshSubscription();
      if (cancelled) return;
      if (isPremium) {
        toast.success("Bem-vindo ao Premium! 🎉", {
          id: toastId,
          description: "Tudo desbloqueado. Aproveite!",
        });
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        toast.message("Pagamento recebido!", {
          id: toastId,
          description:
            "A liberação pode levar alguns instantes. Atualize a página em seguida.",
        });
        return;
      }
      setTimeout(tick, 2000);
    };
    setTimeout(tick, 800);
    return () => {
      cancelled = true;
      toast.dismiss(toastId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.checkout]);

  // Once isPremium flips on while a checkout success was just handled, swap
  // the toast — this also catches the case where realtime delivers first.
  useEffect(() => {
    if (search.checkout === "success" && isPremium) {
      toast.success("Bem-vindo ao Premium! 🎉");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  // Stable fallback palette for categories that have no explicit color
  // (custom categories created without a color, or any unknown name).
  const FALLBACK_PALETTE = [
    "oklch(0.72 0.18 30)",
    "oklch(0.72 0.18 80)",
    "oklch(0.72 0.18 140)",
    "oklch(0.72 0.18 200)",
    "oklch(0.72 0.18 260)",
    "oklch(0.72 0.18 320)",
  ];
  const colorForCategory = (name: string, idx: number) => {
    const custom = customCategories.find((c) => c.name === name);
    if (custom?.color) return custom.color;
    if (CATEGORY_COLORS[name]) return CATEGORY_COLORS[name];
    return FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length];
  };

  const stats = useMemo(() => {
    const thisMonth = transactions.filter((t) => sameMonth(t.date));
    const totalBalance = transactions.reduce(
      (a, t) => a + (t.type === "income" ? t.amount : -t.amount),
      0
    );
    const income = thisMonth.filter((t) => t.type === "income").reduce((a, t) => a + t.amount, 0);
    const expense = thisMonth.filter((t) => t.type === "expense").reduce((a, t) => a + t.amount, 0);
    const savings = income - expense;
    const goalsSaved = goals.reduce((a, g) => a + (g.current ?? 0), 0);
    const investmentsValue = (investmentData?.fixedIncome ?? []).reduce((a, item) => a + item.updatedValue, 0)
      + (investmentData?.marketAssets ?? []).reduce((a, item) => a + item.currentValue, 0);
    const patrimonio = totalBalance + goalsSaved + investmentsValue;

    const byCat = new Map<Category, number>();
    thisMonth
      .filter((t) => t.type === "expense")
      .forEach((t) => byCat.set(t.category, (byCat.get(t.category) ?? 0) + t.amount));
    const pieData = Array.from(byCat.entries()).map(([name, value], idx) => ({
      name,
      value,
      color: colorForCategory(name, idx),
    }));

    // Last 14 days area data — compare by LOCAL calendar day so a transaction
    // dated "today" only changes today's bar, never yesterday's.
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const days: { day: string; saldo: number; key: string }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      days.push({
        day: `${d.getDate()}/${d.getMonth() + 1}`,
        saldo: 0,
        key: dayKey(d),
      });
    }

    const ordered = [...transactions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    days.forEach((entry) => {
      const acc = ordered
        .filter((t) => dayKey(new Date(t.date)) <= entry.key)
        .reduce((a, t) => a + (t.type === "income" ? t.amount : -t.amount), 0);
      entry.saldo = acc;
    });

    return { totalBalance, income, expense, savings, patrimonio, goalsSaved, investmentsValue, pieData, days };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, customCategories, goals, investmentData]);

  const insights = useMemo(() => generateInsights(transactions), [transactions]);
  const forecast = useMemo(() => computeForecast(transactions), [transactions]);
  const recent = transactions.slice(0, 6);

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      {/* Header */}
      <header>
        <p className="text-sm text-muted-foreground">Olá, {displayName} 👋</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold mt-1">
          Bem-vindo ao seu painel
        </h1>
      </header>

      {/* First-run onboarding checklist (hides itself once complete or dismissed) */}
      <OnboardingChecklist userId={user?.id} transactions={transactions} />

      {/* Premium upsell banner — only for free users */}
      {!isPremium && (
        <PremiumDialog
          trigger={
            <button
              type="button"
              className="group w-full flex items-center justify-between gap-4 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-5 py-3.5 hover:border-primary/50 hover:from-primary/15 transition-all text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 size-9 rounded-xl bg-primary/15 flex items-center justify-center">
                  <Sparkles className="size-4 text-primary-glow" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    Desbloqueie metas, transações e categorias ilimitadas
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    A partir de R$ 13,33/mês — IA, previsões e tudo sem limite.
                  </p>
                </div>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary-glow group-hover:gap-1.5 transition-all">
                Assinar <ArrowRight className="size-3.5" />
              </span>
            </button>
          }
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Saldo em conta"
          value={formatBRL(stats.totalBalance)}
          icon={<Wallet className="size-4" />}
          variant="primary"
        />
        <StatCard
          label="Recebido no mês"
          value={formatBRL(stats.income)}
          delta="Receitas"
          trend="up"
          icon={<TrendingUp className="size-4 text-success" />}
        />
        <StatCard
          label="Gasto no mês"
          value={formatBRL(stats.expense)}
          delta="Despesas"
          trend="down"
          icon={<TrendingDown className="size-4 text-destructive" />}
        />
        <StatCard
          label="Patrimônio"
          value={formatBRL(stats.patrimonio)}
          delta={
            stats.investmentsValue > 0
              ? `${formatBRL(stats.investmentsValue)} investidos`
              : stats.goalsSaved > 0
              ? `Inclui ${formatBRL(stats.goalsSaved)} em metas`
              : stats.patrimonio >= 0
                ? "No azul"
                : "No vermelho"
          }
          trend={stats.patrimonio >= 0 ? "up" : "down"}
          icon={<PiggyBank className="size-4" />}
        />
      </div>

      {/* Insights */}
      <section>
        <h2 className="font-display text-lg font-semibold mb-3">Insights inteligentes</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {insights.map((i) => (
            <InsightCard key={i.id} insight={i} />
          ))}
        </div>
      </section>

      {/* AI chat spotlight — highlighted prominently */}
      <AIChatSpotlight isPremium={isPremium} />

      {/* Previsão financeira */}
      <section>
        <h2 className="font-display text-lg font-semibold mb-3">Olhar para o futuro</h2>
        {isPremium ? (
          <ForecastCard forecast={forecast} />
        ) : (
          <PremiumDialog
            trigger={
              <button
                type="button"
                className="w-full text-left rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 hover:border-primary/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 size-12 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow">
                    <Sparkles className="size-5 text-primary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-semibold mb-1">
                      Previsão financeira • Premium
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Veja seu saldo projetado, riscos de ficar negativo e quanto deve sobrar
                      no fim do mês — tudo calculado com base no seu padrão real.
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-glow group-hover:gap-1.5 transition-all">
                      Desbloquear <ArrowRight className="size-3.5" />
                    </span>
                  </div>
                </div>
              </button>
            }
          />
        )}
      </section>

      {/* Charts row */}
      <section className="grid lg:grid-cols-3 gap-4">
        {/* Saldo evolução */}
        <div className="lg:col-span-2 rounded-3xl bg-gradient-card border border-border/40 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold">Evolução do saldo</h3>
              <p className="text-xs text-muted-foreground">Últimos 14 dias</p>
            </div>
          </div>
          <div className="h-64 -mx-2">
            <Suspense fallback={<ChartFallback />}>
              <BalanceTrendChart data={stats.days} />
            </Suspense>
          </div>
        </div>

        {/* Pizza categorias */}
        <div className="rounded-3xl bg-gradient-card border border-border/40 p-6">
          <h3 className="font-display font-semibold mb-1">Gastos por categoria</h3>
          <p className="text-xs text-muted-foreground mb-4">Este mês</p>
          {stats.pieData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">
              Sem despesas neste mês ainda.
            </p>
          ) : (
            <>
              <div className="h-44">
                <Suspense fallback={<ChartFallback />}>
                  <CategoryPieChart data={stats.pieData} />
                </Suspense>
              </div>
              <ul className="mt-3 space-y-1.5">
                {[...stats.pieData]
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 4)
                  .map((d) => (
                    <li key={d.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <span className="size-2 rounded-full" style={{ background: d.color }} />
                        {d.name}
                      </span>
                      <span className="font-medium tabular-nums">{formatBRL(d.value)}</span>
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      </section>

      {/* Recent transactions */}
      <section className="rounded-3xl bg-gradient-card border border-border/40 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-semibold">Últimas transações</h3>
        </div>
        <TransactionList transactions={recent} />
      </section>

      {/* Floating AI assistant button */}
      <AskAIDialog isPremium={isPremium} />
    </div>
  );
}
